import React, { useState, useMemo, useEffect } from 'react';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { queryATSProcessingsByStatusCreatedDateIndex, querySFTPProcessingsByStatusCreatedDateIndex, querySFTPProcessingsByCompanyIdCreatedDateIndex, queryATSProcessingsByCompanyIdCreatedDateIndex, updateATSProcessing, updateSFTPProcessing } from 'src/_shared/api/graphql/custom/ats-processing';

const ProcessingRecordsList = (props) => {
  const { 
    client,
    currentUser
  } = props;
  const [data, setData] = useState(props.data || [])
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSyncType, setFilterSyncType] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentView, setCurrentView] = useState('all'); // 'all' or 'processing'
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [nextRefreshIn, setNextRefreshIn] = useState(30);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchATSData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(queryATSProcessingsByCompanyIdCreatedDateIndex),
        variables: {
          companyId: currentUser?.companyId,
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.queryATSProcessingsByCompanyIdCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchATSData(response.nextToken, updatedData);
      } else {
        return updatedData;
      }
    } catch (error) {
      console.error('Error fetching ATS processing data:', error);
      return accumulatedData;
    }
  };

  const fetchATSProcessingData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(queryATSProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'processing',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.queryATSProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchATSProcessingData(response.nextToken, updatedData);
      } else {
        return updatedData;
      }
    } catch (error) {
      console.error('Error fetching ATS processing data:', error);
      return accumulatedData;
    }
  };

  const fetchATSPendingData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(queryATSProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'pending',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.queryATSProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchATSPendingData(response.nextToken, updatedData);
      } else {
        return updatedData;
      }
    } catch (error) {
      console.error('Error fetching ATS pending data:', error);
      return accumulatedData;
    }
  };

  const fetchATSFailedData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(queryATSProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'failed',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.queryATSProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchATSFailedData(response.nextToken, updatedData);
      } else {
        return updatedData;
      }
    } catch (error) {
      console.error('Error fetching ATS failed data:', error);
      return accumulatedData;
    }
  };

  const fetchSFTPData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(querySFTPProcessingsByCompanyIdCreatedDateIndex),
        variables: {
          companyId: currentUser?.companyId,
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.querySFTPProcessingsByCompanyIdCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchSFTPData(response.nextToken, updatedData);
      } else {
        return updatedData.map(item => {
          return { ...item, atsType: 'SFTP'}
        });
      }
    } catch (error) {
      console.error('Error fetching SFTP processing data:', error);
      return accumulatedData;
    }
  };

  const fetchSFTPProcessingData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(querySFTPProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'processing',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.querySFTPProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchSFTPProcessingData(response.nextToken, updatedData);
      } else {
        return updatedData.map(item => {
          return { ...item, atsType: 'SFTP'}
        });
      }
    } catch (error) {
      console.error('Error fetching SFTP processing data:', error);
      return accumulatedData;
    }
  };

  const fetchSFTPPendingData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(querySFTPProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'pending',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.querySFTPProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchSFTPPendingData(response.nextToken, updatedData);
      } else {
        return updatedData.map(item => {
          return { ...item, atsType: 'SFTP'}
        });
      }
    } catch (error) {
      console.error('Error fetching SFTP pending data:', error);
      return accumulatedData;
    }
  };

  const fetchSFTPFailedData = async (nextToken = null, accumulatedData = []) => {
    try {
      const result = await client.query({  
        query: gql(querySFTPProcessingsByStatusCreatedDateIndex),
        variables: {
          status: 'failed',
          first: 1000,
          after: nextToken
        },
        fetchPolicy: 'network-only'
      });
      
      const response = result?.data?.querySFTPProcessingsByStatusCreatedDateIndex;
      const newItems = response?.items || [];
      const updatedData = [...accumulatedData, ...newItems];
      
      if (response?.nextToken) {
        return await fetchSFTPFailedData(response.nextToken, updatedData);
      } else {
        return updatedData.map(item => {
          return { ...item, atsType: 'SFTP'}
        });
      }
    } catch (error) {
      console.error('Error fetching SFTP failed data:', error);
      return accumulatedData;
    }
  };

  const fetchAllData = async () => {
    if (currentUser) {
      setIsRefreshing(true);
      try {
        const [atsData, atsProcessingData, atsPendingData, atsFailedData, sftpData, sftpProcessingData, sftpPendingData, sftpFailedData] = await Promise.all([
          fetchATSData(),
          fetchATSProcessingData(),
          fetchATSPendingData(),
          fetchATSFailedData(),
          fetchSFTPData(),
          fetchSFTPProcessingData(),
          fetchSFTPPendingData(),
          fetchSFTPFailedData()
        ]);

        const atsDataWithSource = atsData.map(item => ({ ...item, source: 'ATS' }));
        const atsProcessingDataWithSource = atsProcessingData.map(item => ({ ...item, source: 'ATS' }));
        const atsPendingDataWithSource = atsPendingData.map(item => ({ ...item, source: 'ATS' }));
        const atsFailedDataWithSource = atsFailedData.map(item => ({ ...item, source: 'ATS' }));
        const sftpDataWithSource = sftpData.map(item => ({ ...item, source: 'SFTP' }));
        const sftpProcessingDataWithSource = sftpProcessingData.map(item => ({ ...item, source: 'SFTP'}));
        const sftpPendingDataWithSource = sftpPendingData.map(item => ({ ...item, source: 'SFTP'}));
        const sftpFailedDataWithSource = sftpFailedData.map(item => ({ ...item, source: 'SFTP'}));

        // Combine all data and remove duplicates by ID
        const allData = [
          ...atsProcessingDataWithSource, 
          ...sftpProcessingDataWithSource, 
          ...atsPendingDataWithSource,
          ...sftpPendingDataWithSource,
          ...atsFailedDataWithSource,
          ...sftpFailedDataWithSource,
          ...atsDataWithSource, 
          ...sftpDataWithSource
        ];

        // Remove duplicates by ID - keep the first occurrence
        const uniqueData = allData.filter((item, index, self) => 
          index === self.findIndex(t => t.id === item.id)
        );

        const combinedData = uniqueData.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0)).map(item => {
            return { ...item, result: (item?.result || '').trim()}
        });
        setData(combinedData);
      } catch (error) {
        console.error('Error fetching processing data:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchAllData();
    setNextRefreshIn(30);
  };

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
    if (!autoRefreshEnabled) {
      setNextRefreshIn(30);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [currentUser, client]);

  // Auto-refresh and clock effect
  useEffect(() => {
    let refreshIntervalId;
    let countdownId;
    let clockId;

    // Set up live clock for processing durations (always running)
    clockId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    if (autoRefreshEnabled && currentUser) {
      // Set up countdown timer
      countdownId = setInterval(() => {
        setNextRefreshIn((prev) => {
          if (prev <= 1) {
            return 25; // Reset to 25 when it reaches 0
          }
          return prev - 1;
        });
      }, 1000);

      // Set up refresh interval
      refreshIntervalId = setInterval(() => {
        fetchAllData();
        setNextRefreshIn(25);
      }, 25000); // 25 seconds
    }

    return () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
      if (countdownId) {
        clearInterval(countdownId);
      }
      if (clockId) {
        clearInterval(clockId);
      }
    };
  }, [autoRefreshEnabled, currentUser]);

  // Get currently processing items
  const processingItems = useMemo(() => {
    return (data || []).filter(record => record.status === 'processing');
  }, [data]);

  // Get pending items
  const pendingItems = useMemo(() => {
    return (data || []).filter(record => record.status === 'pending');
  }, [data]);

  // Get failed items
  const failedItems = useMemo(() => {
    return (data || []).filter(record => record.status === 'failed');
  }, [data]);

  // Main filtered data
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = data || [];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(record => record.status === filterStatus);
    }
    
    if (filterSyncType !== 'all') {
      filtered = filtered.filter(record => record.syncType === filterSyncType);
    }
    
    return filtered.filter(record => record.companyId === props.currentUser.companyId).sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'createdDate' || sortBy === 'startDate' || sortBy === 'endDate') {
        aVal = new Date(a[sortBy] || 0);
        bVal = new Date(b[sortBy] || 0);
      } else {
        aVal = a[sortBy] || '';
        bVal = b[sortBy] || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [data, sortBy, sortOrder, filterStatus, filterSyncType]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedRecords.slice(startIndex, endIndex);
  }, [filteredAndSortedRecords, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedRecords.length / pageSize);
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, filteredAndSortedRecords.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterSyncType, sortBy, sortOrder]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return '#ff7a00';
      case 'completed': return '#52c41a';
      case 'failed': return '#ff4d4f';
      case 'pending': return '#d46b08';
      case 'cancelled': return '#8c8c8c';
      default: return '#d9d9d9';
    }
  };

  const getSyncTypeColor = (syncType) => {
    switch (syncType) {
      case 'candidate': return '#1890ff';
      case 'job': return '#722ed1';
      case 'employee': return '#13c2c2';
      default: return '#d9d9d9';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const formatElapsedTime = (startDate) => {
    if (!startDate) return '-';
    
    const start = new Date(startDate);
    const now = currentTime;
    const diffMs = now - start;
    
    if (diffMs < 0) return '0s';
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const uniqueStatuses = [...new Set(data.map(r => r.status))];
  const uniqueSyncTypes = [...new Set(data.map(r => r.syncType))];

  // Render table rows component to avoid duplication
  const renderTableRows = (records) => {
    return records.map((record, index) => (
      <tr 
        key={record.id} 
        style={{ 
          borderBottom: index < records.length - 1 ? '1px solid #f0f0f0' : 'none'
        }}
        onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#fafafa'}
        onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
      >
        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500' }}>
          {record.companyName}
        </td>
        <td style={{ padding: '12px 16px', fontSize: '14px' }}>
          {record.atsType}
        </td>
        <td style={{ padding: '12px 16px' }}>
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: getSyncTypeColor(record.syncType),
            color: 'white'
          }}>
            {record.syncType}
          </span>
        </td>
        <td style={{ padding: '12px 16px' }}>
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: getStatusColor(record.status),
            color: 'white'
          }}>
            {record.status}
          </span>
        </td>
        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666' }}>
          {formatTimestamp(record.createdDate)}
        </td>
        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
          {record.status === 'processing' && record.startDate ? (
            <code style={{ 
              backgroundColor: '#fff2e6', 
              color: '#ff7a00',
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid #ffd6a6'
            }}>
              {formatElapsedTime(record.startDate)}
            </code>
          ) : (
            <code style={{ 
              backgroundColor: '#f0f0f0', 
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px'
            }}>
              {record.processTime || '-'}
            </code>
          )}
        </td>
        <td style={{ padding: '12px 16px', fontSize: '13px', maxWidth: '200px' }}>
          {(record.result || '').toLowerCase().includes('error') ? (
            <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
              {(record.result || '').length > 100 ? (record.result || '').substring(0, 100) + '...' : record.result}
            </span>
          ) : (
            <span style={{ color: '#52c41a' }}>{(record.result || '').length > 100 ? (record.result || '').substring(0, 100) + '...' : record.result}</span>
          )}
        </td>
        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
          <button
            onClick={() => setSelectedRecord(record)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#40a9ff'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#1890ff'}
          >
            View Details
          </button>
        </td>
      </tr>
    ));
  };

  // Render table header component to avoid duplication
  const renderTableHeader = () => (
    <thead>
      <tr style={{ backgroundColor: '#fafafa' }}>
        <th 
          onClick={() => handleSort('companyName')}
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #e8e8e8',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            userSelect: 'none'
          }}
        >
          Company {sortBy === 'companyName' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>
        <th 
          onClick={() => handleSort('atsType')}
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #e8e8e8',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            userSelect: 'none'
          }}
        >
          ATS Type {sortBy === 'atsType' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>
        <th 
          onClick={() => handleSort('syncType')}
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #e8e8e8',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            userSelect: 'none'
          }}
        >
          Sync Type {sortBy === 'syncType' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>
        <th 
          onClick={() => handleSort('status')}
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #e8e8e8',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            userSelect: 'none'
          }}
        >
          Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>
        <th 
          onClick={() => handleSort('createdDate')}
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #e8e8e8',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            userSelect: 'none'
          }}
        >
          Created {sortBy === 'createdDate' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>
        <th style={{
          padding: '12px 16px',
          textAlign: 'left',
          borderBottom: '1px solid #e8e8e8',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          Duration
        </th>
        <th style={{
          padding: '12px 16px',
          textAlign: 'left',
          borderBottom: '1px solid #e8e8e8',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          Result
        </th>
        <th style={{
          padding: '12px 16px',
          textAlign: 'center',
          borderBottom: '1px solid #e8e8e8',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          Actions
        </th>
      </tr>
    </thead>
  );

  return (
    <div style={{ 
      paddingBottom: '24px', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#f5f5f5',
    }}>
        
      {/* CSS Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>

      {/* Currently Processing Items - Always Visible */}
      {(processingItems.length > 0 || pendingItems.length > 0 || failedItems.length > 0 ) && (<h2 style={{ marginBottom: 10, fontSize: '24px', fontWeight: '600', }}>
            ATS Processing Records:
          </h2>)}
      {processingItems.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '2px solid #ff7a00'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px' 
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#ff7a00',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: '#ff7a00',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></span>
              Currently Processing ({processingItems.length} items)
            </h3>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e8e8e8', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              {renderTableHeader()}
              <tbody>
                {renderTableRows(processingItems)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Items - Always Visible */}
      {pendingItems.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '2px solid #d46b08'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px' 
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#d46b08',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: '#d46b08',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></span>
              Pending Items ({pendingItems.length} items)
            </h3>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e8e8e8', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              {renderTableHeader()}
              <tbody>
                {renderTableRows(pendingItems)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Failed Items - Always Visible */}
      {failedItems.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '2px solid #ff4d4f'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px' 
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: '#ff4d4f',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></span>
              Recently Failed ({failedItems.length} items)
            </h3>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e8e8e8', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              {renderTableHeader()}
              <tbody>
                {renderTableRows(failedItems)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* Header with Refresh Button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Current Company ATS Processing Records ({filteredAndSortedRecords.length} total)
          </h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Auto-refresh toggle and status */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#fafafa',
              fontSize: '14px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoRefreshEnabled}
                  onChange={toggleAutoRefresh}
                  style={{ cursor: 'pointer' }}
                />
                Auto-refresh
              </label>
              {autoRefreshEnabled && (
                <span style={{ color: '#666', fontSize: '12px' }}>
                  Next: {nextRefreshIn}s
                </span>
              )}
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                padding: '8px 16px',
                backgroundColor: isRefreshing ? '#f5f5f5' : '#52c41a',
                color: isRefreshing ? '#999' : 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isRefreshing ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid #999',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Refreshing...
                </>
              ) : (
                <>Refresh Now</>
              )}
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <label>
                Status: 
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ 
                    marginLeft: '8px', 
                    padding: '6px 12px', 
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Statuses</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div>
              <label>
                Sync Type: 
                <select 
                  value={filterSyncType} 
                  onChange={(e) => setFilterSyncType(e.target.value)}
                  style={{ 
                    marginLeft: '8px', 
                    padding: '6px 12px', 
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Types</option>
                  {uniqueSyncTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div>
            <label>
              Show: 
              <select 
                value={pageSize} 
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{ 
                  marginLeft: '8px', 
                  padding: '6px 12px', 
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </label>
          </div>
        </div>

        {/* Main Table */}
        <div style={{ overflowX: 'auto', border: '1px solid #e8e8e8', borderRadius: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            {renderTableHeader()}
            <tbody>
              {renderTableRows(paginatedRecords)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '16px 0',
            borderTop: '1px solid #e8e8e8'
          }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Showing {startRecord}-{endRecord} of {filteredAndSortedRecords.length} records
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#f5f5f5' : '#1890ff',
                  color: currentPage === 1 ? '#999' : 'white',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Previous
              </button>
              
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: page === currentPage ? '#1890ff' : 'white',
                    color: page === currentPage ? 'white' : '#333',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: page === currentPage ? '600' : 'normal'
                  }}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#1890ff',
                  color: currentPage === totalPages ? '#999' : 'white',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {filteredAndSortedRecords.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#999',
            fontSize: '16px'
          }}>
            No records found matching the current filters.
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedRecord && (
        <ProcessingDetailsModal 
          formatElapsedTime={formatElapsedTime}
          record={selectedRecord} 
          onClose={() => setSelectedRecord(null)}
          client={client}
          onRecordUpdate={(updatedRecord) => {
            setData(prevData => 
              prevData.map(item => 
                item.id === updatedRecord.id ? { ...item, ...updatedRecord } : item
              )
            );
          }}
        />
      )}
    </div>
  );
};

const ProcessingDetailsModal = ({ record, onClose, client, onRecordUpdate, formatElapsedTime }) => {
  const [isRestarting, setIsRestarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleRestart = async () => {
    setIsRestarting(true);
    try {
      const updateInput = {
        id: record.id,
        status: 'pending'
      };

      let result;
      if (record.atsType === 'SFTP') {
        result = await client.mutate({
          mutation: gql(updateSFTPProcessing),
          variables: {
            input: updateInput
          }
        });
      } else {
        result = await client.mutate({
          mutation: gql(updateATSProcessing),
          variables: {
            input: updateInput
          }
        });
      }

      const updatedRecord = { ...record, status: 'pending' };
      onRecordUpdate(updatedRecord);
      onClose();
      
    } catch (error) {
      console.error('Error restarting process:', error);
      alert('Failed to restart process. Please try again.');
    } finally {
      setIsRestarting(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      const updateInput = {
        id: record.id,
        status: 'cancelled'
      };

      let result;
      if (record.atsType === 'SFTP') {
        result = await client.mutate({
          mutation: gql(updateSFTPProcessing),
          variables: {
            input: updateInput
          }
        });
      } else {
        result = await client.mutate({
          mutation: gql(updateATSProcessing),
          variables: {
            input: updateInput
          }
        });
      }

      const updatedRecord = { ...record, status: 'cancelled' };
      onRecordUpdate(updatedRecord);
      onClose();
      
    } catch (error) {
      console.error('Error cancelling process:', error);
      alert('Failed to cancel process. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const parsedLogs = useMemo(() => {
    if (!record?.logs) return [];
    
    const logLines = record.logs.split('\n').filter(line => line.trim());
    const logs = [];
    
    logLines.forEach((line, index) => {
      const createdMatch = line.match(/Created at ([\d-T:.Z]+) [Ff]or [Pp]rocessing (.+)/);
      const startedMatch = line.match(/Started processing at ([\d-T:.Z]+) by (?:ATS )?worker (.+?) \(Process (.+?)\)/);
      const completedMatch = line.match(/Completed processing at ([\d-T:.Z]+) by (?:ATS )?worker (.+?) \(Process (.+?)\)/);
      
      if (createdMatch) {
        logs.push({
          key: index,
          timestamp: createdMatch[1],
          action: 'Created',
          description: `Processing ${createdMatch[2]}`,
          workerId: null,
          processInfo: null
        });
      } else if (startedMatch) {
        logs.push({
          key: index,
          timestamp: startedMatch[1],
          action: 'Started',
          description: 'Processing started',
          workerId: startedMatch[2],
          processInfo: startedMatch[3]
        });
      } else if (completedMatch) {
        logs.push({
          key: index,
          timestamp: completedMatch[1],
          action: 'Completed',
          description: 'Processing completed',
          workerId: completedMatch[2],
          processInfo: completedMatch[3]
        });
      }
    });
    
    return logs;
  }, [record?.logs]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'Created': return '#1890ff';
      case 'Started': return '#fa8c16';
      case 'Completed': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            Processing Details - {record.companyName}
          </h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Cancel Button - Only show for failed or pending processes */}
            {(record.status === 'failed' || record.status === 'pending') && (
              <button
                onClick={handleCancel}
                disabled={isCancelling}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isCancelling ? '#f5f5f5' : '#ff4d4f',
                  color: isCancelling ? '#999' : 'white',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title="Cancel this process"
              >
                {isCancelling ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      border: '2px solid #999',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    Cancelling...
                  </>
                ) : (
                  <>Cancel Process</>
                )}
              </button>
            )}
            
            {/* Restart Button */}
            <button
              onClick={handleRestart}
              disabled={isRestarting || record.status === 'processing'}
              style={{
                padding: '8px 16px',
                backgroundColor: (isRestarting || record.status === 'processing') ? '#f5f5f5' : '#ff7a00',
                color: (isRestarting || record.status === 'processing') ? '#999' : 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                cursor: (isRestarting || record.status === 'processing') ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              title={record.status === 'processing' ? 'Cannot restart a process that is currently running' : 'Restart this process'}
            >
              {isRestarting ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid #999',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Restarting...
                </>
              ) : (
                <>Restart Process</>
              )}
            </button>
            
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>

        <div style={{ 
          display: 'grid',
          gap: '16px', 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#fafafa',
          borderRadius: '6px',
          border: '1px solid #e8e8e8'
        }}>
          <div><strong>ATS Type:</strong> {record.atsType}</div>
          <div><strong>Process:</strong> {record.processId}:{record.processName}</div>
          <div><strong>Sync Type:</strong> {record.syncType}</div>
          <div><strong>Status:</strong> {record.status}</div>
          {record.status === 'completed' && record.endDate && (<div><strong>Completed: </strong>  <code style={{ 
                backgroundColor: '#f0f0f0', 
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px'
            }}>{formatTimestamp(record.endDate)}</code></div>  ) }
          <div><strong>Process Time: </strong>  
          {record.status === 'processing' && record.startDate ? (
            <code style={{ 
              backgroundColor: '#fff2e6', 
              color: '#ff7a00',
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid #ffd6a6'
            }}>
              {formatElapsedTime(record.startDate)}
            </code>
          ) : (
            <code style={{ 
              backgroundColor: '#f0f0f0', 
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px'
            }}>
              {record.processTime || '-'}
            </code>
          )}
        </div>
          <div><strong>Queue Time:</strong>   <code style={{ 
              backgroundColor: '#f0f0f0', 
              padding: '2px 6px', 
              borderRadius: '3px',
              fontSize: '12px'
            }}>
              {record.processTimeInQueue || '-'}
            </code></div>
          {record?.file && (<div><strong>File:</strong> {record.file}</div>)}
        </div>
 
        {record.result && (
          <div style={{ marginBottom: '20px' }}>
            <strong>Result:</strong>
            <div style={{ 
              marginTop: '8px', 
              padding: '12px', 
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              border: '1px solid #e8e8e8'
            }}>
             {(record.result || '').toLowerCase().includes('error') ? (
                <code style={{ 
                    color: '#ff4d4f', 
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap'  // This preserves newlines
                }}>
                    {record.result}
                </code>
                ) : (
                <code style={{ 
                    color: '#52c41a', 
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap'  // This preserves newlines
                }}>
                    {record.result}
                </code>
            )}
            </div>
          </div>
        )}

        <div>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
            Processing Timeline ({parsedLogs.length} entries)
          </h4>
          
          <div style={{ overflowX: 'auto', border: '1px solid #e8e8e8', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', fontWeight: '600', fontSize: '14px' }}>
                    Timestamp
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', fontWeight: '600', fontSize: '14px' }}>
                    Action
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', fontWeight: '600', fontSize: '14px' }}>
                    Description
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', fontWeight: '600', fontSize: '14px' }}>
                    Worker ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {parsedLogs.map((log, index) => (
                  <tr 
                    key={log.key} 
                    style={{ 
                      borderBottom: index < parsedLogs.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}
                  >
                    <td style={{ padding: '12px 16px', fontSize: '12px' }}>
                      <code style={{ 
                        backgroundColor: '#f0f0f0', 
                        padding: '2px 6px', 
                        borderRadius: '3px',
                        fontSize: '11px'
                      }}>
                        {formatTimestamp(log.timestamp)}
                      </code>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: getActionColor(log.action),
                        color: 'white'
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                      {log.description}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px' }}>
                      {log.workerId ? (
                        <code style={{ 
                          backgroundColor: '#f0f0f0', 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          fontSize: '11px'
                        }}>
                          {log.workerId.substring(0, 8)}...
                        </code>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withApollo(ProcessingRecordsList);